import qs from 'qs';
import { UtilsAccount } from 'utils/account';
import LS from 'utils/bom-dom-manipulation/local-storage';
import { UNCATEGORIZED } from 'utils/constants/casino/casino_categories';
import { CUSTOM_CATEGORIES } from 'utils/constants/casino/custom-categories';
import { CASINO_MULTIVIEW_ENABLED_QUERY_VALUE } from 'utils/constants/casino/url-queries';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { gameUrl } from 'utils/generic/game-url';
import {
  getCasinoParams,
  getCurrentPageArea,
  mountPoint,
  sbRedirect
} from 'utils/generic/route-manipulation';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { isMobile } from 'utils/is-mobile';
import { fromAllCapitalizedToDasherized } from 'utils/string-manipulation/dasherized-to-all-capitalized';
import {
  ECasinoGameMode,
  ECasinoGameView,
  ECasinoGameViewOrder
} from 'interfaces/casino-data';
import { NewCasinoGame } from 'interfaces/new-casino';
import { AFTER_SIGN_IN_ACTION_TYPES, AfterSignIn } from 'services/afterSignIn';
import { UtilsCasino } from './index';
import { browserHistory } from 'configure/history';
import { addQuery, saveCurrentLocation } from 'components/QueryRoute';
import store from 'store';
import { batchActions, setSelectedCasinoGame } from 'store/actions';
import {
  addCachedGame,
  addCachedGameId,
  setCachedGameInactive,
  setCachedGameMode,
  setCachedGamesInactive
} from 'store/actions/new-casino';

const RESOLVE_AFTER_SIGNIN_DURATION = 300;
export interface IConstructGameViewUrl {
  game: NewCasinoGame;
  mode: ECasinoGameMode;
  view?: ECasinoGameView;
  selectedCategory?: string;
  tournament?: {
    id: string;
  };
  gameIndexToChange?: 0 | 1;
  queriesToRemove?: string[];
}

export const constructAndRedirectToGameView = ({
  game,
  mode,
  view = ECasinoGameView.SINGLE,
  selectedCategory,
  tournament,
  gameIndexToChange,
  queriesToRemove
}: IConstructGameViewUrl): void => {
  const isUserLoggedIn = UtilsAccount.checkIsUserLoggedIn();

  // This function is responsible for redirecting out to the game page
  const redirectToGameUrl = () => {
    window.open(
      gameUrl({
        gameId: game.extearnal_game_id,
        gameType: mode,
        gameOptions: game.game_options
      }),
      '_self'
    );
  };

  // This function is responsible for changing the route in the project session
  const transitionToGameUrl = (
    saveUrl?: boolean,
    showInUrl?: boolean,
    firstGameMode?: string
  ) => {
    return new Promise(resolve => {
      !saveUrl && LS.setItem(storageKeyName('casino', 'GAMES_FETCHED'), 'true');

      if (view === ECasinoGameView.SINGLE) {
        const gameCategorySection = `${
          SpringConfigs.DECENTRALIZED_CASINO_URL
            ? game.cats[0]
            : game.cats[0]?.id || UNCATEGORIZED
        }`;

        const providerSection = `${fromAllCapitalizedToDasherized(
          game.provider_title
        )}`;

        const gameSection = `${game.id}-${
          game.extearnal_game_id
        }-${fromAllCapitalizedToDasherized(game.name)}`;

        const categorySection = selectedCategory || CUSTOM_CATEGORIES.all.id;

        const queryObj = qs.parse(window.location.search, {
          ignoreQueryPrefix: true
        });

        const queriesToRemoveObj = (queriesToRemove || []).reduce(
          (acc, query) => {
            acc[query] = undefined;

            return acc;
          },
          {} as Record<string, string | undefined>
        );

        const relativePath = [
          categorySection,
          gameCategorySection,
          providerSection,
          gameSection
        ];

        const currentPageArea = getCurrentPageArea();

        if (currentPageArea && currentPageArea !== 'casino') {
          const queryObject: Record<string, string> = {
            mode
          };

          if (mode === 'real' && !isUserLoggedIn) {
            queryObject.accounts = '*';
            queryObject.login = '*';
          }

          sbRedirect(
            `${SpringConfigs.PAGE_URLS.casino}/${
              SpringConfigs.CASINO_MOUNT_PATH
            }/${relativePath.join('/')}?${qs.stringify(queryObject)}`,
            false,
            true
          );
          saveCurrentLocation();

          setTimeout(() => {
            resolve(true);
          }, RESOLVE_AFTER_SIGNIN_DURATION);

          return;
        }

        if (saveUrl && (window.hasPopup || showInUrl)) {
          const gameRoute = {
            pathname: `${mountPoint(
              window.getPathname(),
              false,
              true
            )}/${relativePath.join('/')}`,
            search: qs.stringify({
              ...queryObj,
              mode,
              tournamentId: tournament?.id,
              ...queriesToRemoveObj,
              ...(showInUrl
                ? {}
                : {
                    accounts: undefined,
                    login: undefined,
                    register: undefined
                  })
            })
          };

          if (showInUrl) {
            history.pushState(
              {},
              '',
              `${gameRoute.pathname}?${gameRoute.search}`
            );
          }

          if (window.hasPopup) {
            LS.setItem(
              storageKeyName('casino', 'SELECTED_GAME_URL'),
              JSON.stringify(gameRoute)
            );
          }

          return;
        }

        store.dispatch(setSelectedCasinoGame(game));

        // new way to add cached game
        store.dispatch(addCachedGameId(game.extearnal_game_id));
        store.dispatch(
          batchActions(
            setCachedGamesInactive(game.extearnal_game_id),
            addCachedGame(game)
          )
        );
        store.dispatch(setCachedGameMode({ id: game.extearnal_game_id, mode }));

        if (
          window.location.pathname.includes(
            `/${SpringConfigs.SPORTSBOOK_MOUNT_PATH}/`
          )
        ) {
          browserHistory.push({
            pathname: `${mountPoint(window.getPathname(), false, true).replace(
              `/${SpringConfigs.CASINO_MOUNT_PATH}`,
              ''
            )}`,
            search: qs.stringify({
              gameId: game.extearnal_game_id,
              mode,
              accounts: undefined,
              login: undefined,
              register: undefined,
              ...(!!queryObj.type && { type: queryObj.type })
            })
          });
          saveCurrentLocation();

          setTimeout(() => {
            resolve(true);
          }, RESOLVE_AFTER_SIGNIN_DURATION);

          return;
        }

        browserHistory.push({
          pathname: `${mountPoint(
            window.getPathname(),
            false,
            true
          )}/${relativePath.join('/')}`,
          search: qs.stringify({
            ...queryObj,
            mode,
            accounts: undefined,
            login: undefined,
            register: undefined,
            tournamentId: tournament?.id,
            ...queriesToRemoveObj
          })
        });
        saveCurrentLocation();

        setTimeout(() => {
          resolve(true);
        }, RESOLVE_AFTER_SIGNIN_DURATION);

        return;
      }

      if (view === ECasinoGameView.MULTI) {
        const searchParams = Object.fromEntries(
          new URLSearchParams(window.location.search)
        );

        // case when multiview is already open, and we want to change one of the games
        if (searchParams.multiview === CASINO_MULTIVIEW_ENABLED_QUERY_VALUE) {
          const gamesInfo =
            UtilsCasino.extractGameInfoFromSearchParams(searchParams);

          const gameToChange = gamesInfo[gameIndexToChange || 0];

          store.dispatch(
            batchActions(
              setCachedGameInactive(gameToChange.id),
              addCachedGame(game)
            )
          );
          store.dispatch(
            batchActions(
              setCachedGameMode({ id: game.extearnal_game_id, mode }),
              setCachedGameMode({
                id: gameToChange.id,
                mode: ECasinoGameMode.FUN
              })
            )
          );

          const queryToUpdate = addQuery({
            [UtilsCasino.encodeGameIdWithPrefix(
              gameToChange.order,
              gameToChange.id
            )]: undefined,
            [UtilsCasino.encodeGameIdWithPrefix(
              gameToChange.order,
              game.extearnal_game_id
            )]: mode
          });

          browserHistory.push({
            ...queryToUpdate,
            search: UtilsCasino.normalizeStringifiedQuery(queryToUpdate.search)
          });
          saveCurrentLocation();

          setTimeout(() => {
            resolve(true);
          }, RESOLVE_AFTER_SIGNIN_DURATION);

          return;
        }

        // case when multiview is not open, and we want to open it
        const currentGameInfo = getCasinoParams();
        const categorySection = selectedCategory || CUSTOM_CATEGORIES.all.id;

        const queryObj = qs.parse(window.location.search, {
          ignoreQueryPrefix: true
        });

        store.dispatch(addCachedGame(game));
        store.dispatch(setCachedGameMode({ id: game.extearnal_game_id, mode }));

        let pathname = `${mountPoint(
          window.getPathname(),
          false,
          true
        )}/${categorySection}`;

        if (
          window.location.pathname.includes(
            `/${SpringConfigs.SPORTSBOOK_MOUNT_PATH}/`
          )
        ) {
          delete queryObj.gameId;

          pathname = pathname.replace(
            `/${SpringConfigs.CASINO_MOUNT_PATH}/${categorySection}`,
            ''
          );
        }

        browserHistory.push({
          pathname,
          search: UtilsCasino.normalizeStringifiedQuery(
            qs.stringify(
              {
                ...queryObj,
                multiview: CASINO_MULTIVIEW_ENABLED_QUERY_VALUE,
                [UtilsCasino.encodeGameIdWithPrefix(
                  ECasinoGameViewOrder.FIRST,
                  currentGameInfo.gameExternalId
                )]: firstGameMode || searchParams.mode,
                [UtilsCasino.encodeGameIdWithPrefix(
                  ECasinoGameViewOrder.SECOND,
                  game.extearnal_game_id
                )]: mode,
                accounts: undefined,
                login: undefined,
                register: undefined,
                mode: undefined
              },
              { addQueryPrefix: true }
            )
          )
        });
        saveCurrentLocation();

        setTimeout(() => {
          resolve(true);
        }, RESOLVE_AFTER_SIGNIN_DURATION);
      }
    });
  };

  /**  CHECKS FOR REAL MODE GAME OPENING  **/

  const currentPageArea = getCurrentPageArea();

  if (
    mode === ECasinoGameMode.REAL &&
    !isUserLoggedIn &&
    (!currentPageArea || currentPageArea === 'casino')
  ) {
    window.hasPopup && transitionToGameUrl(true);

    const searchParams = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );

    const firstGameMode = searchParams.mode;

    let query: { [key: string]: string } = { mode };

    if (searchParams.multiview === CASINO_MULTIVIEW_ENABLED_QUERY_VALUE) {
      for (const param in searchParams) {
        if (param.indexOf(game.extearnal_game_id) >= 0) {
          query = { [param]: mode };
        }
      }
    }

    UtilsAccount.openLoginView({ query });

    transitionToGameUrl(true, true, firstGameMode);

    const transitionFn = transitionToGameUrl.bind(
      undefined,
      false,
      undefined,
      firstGameMode
    );

    const afterSignIn = new AfterSignIn();
    afterSignIn.appendAction(
      AFTER_SIGN_IN_ACTION_TYPES.CONSTRUCT_CASINO_VIEW_URL,
      transitionFn
    );

    return;
  }

  if (!SpringConfigs.CASINO_MOBILE_IFRAME && isMobile()) {
    redirectToGameUrl();
  } else {
    transitionToGameUrl();
  }
};
